import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  Brand,
  BrandCreateFormData,
  brandCreateSchema,
  BrandUpdateFormData,
  brandUpdateSchema,
} from "../../schemas/brandSchema";

interface BrandFormProps {
  initialData?: Partial<Brand>;
  onSubmit: (data: BrandCreateFormData | BrandUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
  isOpen?: boolean;
}

const BrandForm: React.FC<BrandFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
  isOpen = true,
}) => {
  const schema = isUpdate ? brandUpdateSchema : brandCreateSchema;
  const form = useForm<BrandCreateFormData | BrandUpdateFormData>({
    // @ts-ignore - Type mismatch between create and update schemas is expected
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: initialData?.name || "",
      email: initialData?.email || "",
      phoneNumber: initialData?.phoneNumber || "",
      username: initialData?.username || "",
    },
  });

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    reset,
    trigger,
  } = form;

  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      reset({
        name: initialData?.name || "",
        email: initialData?.email || "",
        phoneNumber: initialData?.phoneNumber || "",
        username: initialData?.username || "",
      });
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Brand Name"
          placeholder="Enter brand name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) =>
            setValue("name", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("name")}
          error={errors.name}
        />
        <Input
          label="Email"
          placeholder="Enter email"
          name="email"
          value={watch("email") || ""}
          onChange={(e) =>
            setValue("email", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("email")}
          error={errors.email}
        />
        <Input
          label="Phone Number"
          placeholder="Enter phone number"
          name="phoneNumber"
          value={watch("phoneNumber") || ""}
          onChange={(e) =>
            setValue("phoneNumber", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("phoneNumber")}
          error={errors.phoneNumber}
          allowedChars="numeric"
        />
        {/* Username is create-only: changing it after the fact would silently
            break the credentials the brand was emailed. */}
        {!isUpdate && (
          <Input
            label="Username (optional)"
            placeholder="Derived from the email if left blank"
            name="username"
            value={(watch as any)("username") || ""}
            onChange={(e) =>
              setValue("username" as any, e.target.value, {
                shouldValidate: false,
              })
            }
            onBlur={() => trigger("username" as any)}
            error={(errors as any).username}
          />
        )}
      </div>

      {!isUpdate && (
        <p className="text-sm text-gray-500">
          A password is generated automatically and emailed to this address
          along with the username. The brand can sign in with either.
        </p>
      )}

      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          className="w-full sm:w-auto"
        >
          {isUpdate ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
};

export default BrandForm;
